import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { fmt, errText } from "./shared";

const AREA = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1";

/** "Unable to Fulfil / Return Cashback" — confirmed failures only. */
export function FailDialog({ open, onOpenChange, order, onDone }) {
  const [reason, setReason] = useState("");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setChecked(false); } }, [open]);
  if (!order) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/rewards/fulfilment/orders/${order.id}/fail`, { reason, confirm_return_cashback: checked });
      toast.success(`Order cancelled · ${fmt(order.reserved_minor)} returned to the shopper.`);
      onDone?.();
    } catch (e) {
      toast.error(errText(e, "Could not cancel the order."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gc-fail-dialog">
        <DialogHeader>
          <DialogTitle>Unable to Fulfil / Return Cashback</DialogTitle>
          <DialogDescription>Use only when the card definitively cannot be supplied. Unsure? Mark it for review instead.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex gap-2" data-testid="gc-fail-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          This will cancel the redemption and return the reserved cashback to the Shopper.
        </div>
        <label className="block">
          <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-widest">Internal reason (required)</span>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className={AREA}
                    placeholder="e.g. Supplier confirmed $25 denomination discontinued" data-testid="gc-fail-reason" />
        </label>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="gc-fail-confirm" />
          Return {fmt(order.reserved_minor)} to {order.shopper_reference}. This can only happen once.
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm border border-slate-200" data-testid="gc-fail-cancel">Keep order</button>
          <button disabled={!checked || reason.trim().length < 3 || busy} onClick={submit}
                  className="px-5 py-2 rounded-full text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  data-testid="gc-fail-submit">{busy ? "Returning…" : "Cancel and return cashback"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Generic one-field action: needs-review reason, supplier details, notes. */
export function TextActionDialog({ open, onOpenChange, title, description, fields, submitLabel, onSubmit, testid }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setValues(Object.fromEntries(fields.map((f) => [f.key, f.initial || ""]))); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const ok = fields.every((f) => f.optional || (values[f.key] || "").trim().length >= (f.min || 2));
  const run = async () => {
    setBusy(true);
    try { await onSubmit(values); onOpenChange(false); } catch { /* toast already shown */ } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {fields.map((f) => (
          <label key={f.key} className="block">
            <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-widest">{f.label}</span>
            <textarea rows={f.rows || 2} value={values[f.key] || ""} className={AREA} placeholder={f.placeholder}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} data-testid={`${testid}-${f.key}`} />
          </label>
        ))}
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm border border-slate-200" data-testid={`${testid}-cancel`}>Cancel</button>
          <button disabled={!ok || busy} onClick={run} className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50"
                  data-testid={`${testid}-submit`}>{busy ? "Saving…" : submitLabel}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
